import React from "react";
import {
    Typography,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tooltip,
    Table,
    Stack,
    Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import { scoreLevels } from "../../../../context/global";
import { EuroFormat, NumberFormat } from "../../../../functions/Formatters";
import { ThumbDown, ThumbUp } from "@mui/icons-material";
import { useTheme } from "@emotion/react";

export default function CompareTable({ designs, referenceDesign, payload }) {
    const styling = useTheme();
    const flatKpis = useSelector((state) => state.themes.flatKpis);

    const formatSwitcher = (value, unit) => {
        if (unit === "€") {
            return EuroFormat(value);
        } else {
            return `${NumberFormat(value)} ${unit}`;
        }
    };

    const sizeProps = { sx: { p: 0, m: 0, ml: 1 }, fontSize: "small" };
    
    const GoodIcon = ({ hoverText }) => (
        <Tooltip title={hoverText}>
            <ThumbUp {...sizeProps} color="success" />
        </Tooltip>
    );
    
    const BadIcon = ({ hoverText }) => (
        <Tooltip title={hoverText}>
            <ThumbDown {...sizeProps} color="error" />
        </Tooltip>
    );

    /**
     * Renders an icon based on the provided diff value.
     * @param {number} diff - The diff value.
     * @param {string} [hoverText=""] - The hover text for the icon.
     * @returns {JSX.Element|null} The rendered icon component.
     */
    const DiffSign = (diff, hoverText = "") => {
        switch (diff) {
            case 0:
                return <GoodIcon hoverText={hoverText} />;
            case 1:
                return <GoodIcon hoverText={hoverText} />;
            case 2:
                return (
                    <>
                        <GoodIcon hoverText={hoverText} />
                        {/* <GoodIcon hoverText={hoverText} /> */}
                    </>
                );
            case -1:
                return <BadIcon hoverText={hoverText} />;
            case -2:
                return (
                    <>
                        <BadIcon hoverText={hoverText} />
                        {/* <BadIcon hoverText={hoverText} /> */}
                    </>
                );
            default:
                return null;
        }
    };

    const formattedTotalScore = (score, kpi, compareTo) => {
        let diff = null;
        let label = "";
        let hoverText = "";
        if (!score || score?.value === null || score.value === undefined) {
            return "Geen score gevonden";
        }
        if (kpi.qualitative_scale || kpi.scale_type === 0) {
            if (compareTo !== undefined) {
                diff = score.value - compareTo;
                const compareWord = diff > 0 ? "beter" : "slechter";
                const goalLabel = scoreLevels[compareTo - 1] ? scoreLevels[compareTo - 1].label : "Geen score";
                const scoreLabel = scoreLevels[score.value - 1] ? scoreLevels[score.value - 1].label : "Geen score";
                hoverText = `Deze score ("${scoreLabel}") is ${compareWord} dan het doel van "${goalLabel}".`;
            }
            label = scoreLevels[score.value - 1] ? scoreLevels[score.value - 1].label : "Geen score";
            
        } else {
            let total = 0;
            if (kpi.scale_type === 1) {
                if (score.value instanceof Array) {
                    try {
                        total = score.value.reduce((acc, segment) => acc + segment.values.reduce((acc, layer) => acc + Number(layer.value), 0), 0);
                    } catch {
                        console.log("Error in calculating total score for", kpi.name);
                        total = 0;
                    }
                }
                if (!isNaN(Number(score.value))) {
                    total = Number(score.value);
                }
            }
            if (kpi.scale_type === 2) {
                total = Number(score.value);
            }
            if (compareTo !== undefined) {
                const deltaAbsolute = total - compareTo;
                const deltaRelative = (100 * Math.abs(deltaAbsolute)) / compareTo;
                const compareWord = deltaAbsolute > 0 ? "hoger" : "lager";
                const compareSign = deltaAbsolute > 0 ? "+" : "-";
                const unit = kpi.unit === "%" ? "procentpunten" : kpi.unit;
                hoverText = `Deze score is ${formatSwitcher(Math.abs(deltaAbsolute), unit)} ${compareWord} dan het doel van ${formatSwitcher(
                    compareTo,
                    kpi.unit
                )}. (${compareSign}${deltaRelative.toFixed(1)}%)`;
                if (kpi.maximize) {
                    if (total > compareTo) {
                        diff = total > 1.5 * compareTo ? 2 : 1;
                    } else if (total < compareTo) {
                        diff = total < compareTo / 2 ? -2 : -1;
                    } else {
                        diff = 0;
                    }
                } else if (total > compareTo) {
                    diff = total > 1.5 * compareTo ? -2 : -1;
                } else if (total < compareTo) {
                    diff = total < compareTo / 2 ? 2 : 1;
                } else {
                    diff = 0;
                }
            }

            label = formatSwitcher(total, kpi.unit);
        }
        return (
            <Stack direction="row" spacing={0}>
                <Typography>{label}</Typography>
                <Box>{DiffSign(diff, hoverText)}</Box>
            </Stack>
        );
    };

    // Get all KPIs from loop_kpis, sorted by name
    const allLoopKPIs = payload?.loop?.loop_kpis
        ? [...payload.loop.loop_kpis]
              .filter(loopKpi => loopKpi.is_selected)
              .map(lk => ({...lk, kpi_details: flatKpis[lk.kpi]}))
              .sort((a, b) => a.kpi_details.name.localeCompare(b.kpi_details.name))
        : [];

    return (
        <TableContainer>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell key={"corner_cell"} />
                        {referenceDesign && (
                            <TableCell
                                key={"goal_cell"}
                                sx={{
                                    borderLeft: "1px solid white",
                                }}
                            >
                                <Typography variant="h5" textOverflow={"no-wrap"}>
                                    Doel
                                </Typography>
                            </TableCell>
                        )}
                        {designs.map((design, index) => (
                            <TableCell key={"design_" + index}>
                                <Tooltip title={design.description || 'Geen beschrijving'}>
                                    <Typography variant="h5" textOverflow={"no-wrap"} sx={{ cursor: 'help' }}>
                                        {design.name}
                                    </Typography>
                                </Tooltip>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {/* Cost row */}
                    <TableRow sx={{ backgroundColor: styling.palette.background.default }}>
                        <TableCell>
                            <Typography textOverflow={"no-wrap"}>
                                Kosten
                            </Typography>
                        </TableCell>
                        {referenceDesign && (
                            <TableCell
                                key={"goal_cell"}
                                sx={{
                                    borderLeft: "1px solid white",
                                }}
                            />
                        )}
                        {designs.map((design, index) => (
                            <TableCell key={"design_cost_" + index}>
                                <Typography>{EuroFormat(design.investment_cost)}</Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                    
                    {/* KPI rows */}
                    {allLoopKPIs.map((loopKpi, index) => {
                        const kpi = loopKpi.kpi_details;
                        const goal = loopKpi.loop_goals[0]; // Each loop KPI has a single goal
                        const isEvenRow = (index + 1) % 2 === 0; // +1 because cost row is index 0
                        const rowColor = isEvenRow ? styling.palette.grey?.[100] || '#f5f5f5' : styling.palette.background.default;
                        
                        return (
                            <TableRow key={`kpi_${loopKpi.id}`} sx={{ backgroundColor: rowColor }}>
                                <TableCell sx={{ borderRight: "1px solid white" }}>
                                    <Typography>
                                        {kpi.name}
                                    </Typography>
                                    {kpi.unit && (
                                        <Typography variant="body2">
                                            ({kpi.unit})
                                        </Typography>
                                    )}
                                    {kpi.description && (
                                        <Typography variant="body2" color="text.secondary">
                                            {kpi.description}
                                        </Typography>
                                    )}
                                </TableCell>
                                
                                {/* Goal column (only if reference design exists) */}
                                {referenceDesign && (
                                    <TableCell key={"goal_cell"} sx={{ borderLeft: "1px solid white" }}>
                                        <Typography>
                                            {formattedTotalScore(goal, kpi)}
                                        </Typography>
                                    </TableCell>
                                )}
                                
                                {/* Design score columns */}
                                {designs.map((design, designIndex) => {
                                    // Find the score for this KPI in the design's scores
                                    let score = design.scores?.find(s => s.kpi === kpi.id);
                                    if (!score) {
                                        score = { value: null };
                                    }

                                    return (
                                        <TableCell
                                            key={`design_${design.id}_kpi_${kpi.id}`}
                                            sx={{
                                                borderLeft: `${designIndex === 0 && referenceDesign ? 1 : 0}px solid white`,
                                            }}
                                        >
                                            <Typography>
                                                {formattedTotalScore(score, kpi, goal?.value)}
                                            </Typography>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

